import { PyodideFileJournal } from '../journal/python-journal.helper';
import { PythonImportsHelper } from '../execution/python-imports.helper';
import { FileService } from '../../file/file.service';
import { PythonFileSystemHelper } from '../filesystem/python-filesystem.helper';
import { STEPS_NAMESPACE } from '../../../constants/general.constants';
import { PyodideLockfile } from '../interfaces/python-package.interface';
import { runPythonIsolated } from '../helpers/python-isolated.helper';

/**
 * PythonPackageHelper
 *
 * Manages Python package installation and import processing.
 * Extracted from PythonService to reduce monolithic service size.
 *
 * Responsibilities:
 * - Process imports and load required packages
 * - Verify package availability
 * - Manage package dependencies
 */
export class PythonPackageHelper {
  private importToPackageMap: Map<string, string> | null = null;

  constructor(
    private readonly python: any,
    private readonly fileJournal: PyodideFileJournal,
    private readonly importsHelper?: PythonImportsHelper,
    private readonly fileService?: FileService,
    private readonly fileSystemHelper?: PythonFileSystemHelper,
    private readonly loadedPackages?: Set<string>,
    private readonly workingDir?: string
  ) { }

  /**
   * Process loading packages from imports in the provided content
   * Uses journal-based caching and filters out JS modules
   *
   * @param content - Python code to analyze for imports
   * @param filePath - Optional file path for caching
   * @param jsModules - Set of JS module names to filter out
   */
  async processLoadingPackagesFromImports(
    content: string,
    filePath: string | undefined,
    jsModules: Set<string>
  ): Promise<void> {
    // First pass: load built-in Pyodide packages matching imports (fast, uses lockfile)
    try {
      await this.python.loadPackagesFromImports(content);
    } catch {
      // Non-fatal: fall through to micropip
    }

    const dependencies = this.analyzeDependencies(content, filePath);

    // Filter out JS modules (both camelCase and snake_case) and the
    // "steps" namespace (local step module system, not a PyPI package)
    const pythonPackages = dependencies.filter(pkg => !jsModules.has(pkg) && pkg !== STEPS_NAMESPACE);

    // Use Journal for package tracking
    const newPackages = pythonPackages.filter(pkg =>
      this.fileJournal ? !this.fileJournal.isPackageLoaded(pkg) : !this.loadedPackages?.has(pkg)
    );

    if (newPackages.length === 0) {
      return;
    }

    await this.installPackages(newPackages);
  }

  /**
   * Analyze Python code for import dependencies, using journal cache when available.
   */
  private analyzeDependencies(content: string, filePath: string | undefined): string[] {
    if (filePath) {
      const cached = this.fileJournal.getFileAnalysis(filePath);
      if (cached) {
        return cached;
      }
      const analysis = this.importsHelper?.analyzeImports(content);
      const dependencies = analysis?.dependencies || [];
      if ((analysis?.errors?.length ?? 0) === 0) {
        this.fileJournal.logFileAnalysis(filePath, dependencies);
        return dependencies;
      }
      return PythonPackageHelper.extractImportsViaRegex(content);
    }

    // No caching for user code (changes frequently)
    const analysis = this.importsHelper?.analyzeImports(content);
    if (analysis?.errors?.length) {
      return PythonPackageHelper.extractImportsViaRegex(content);
    }
    return analysis?.dependencies || [];
  }

  /**
   * Invalidate Python's import caches so importlib.find_spec() sees files that
   * were just written into the Pyodide filesystem (e.g. project modules mounted
   * into /home/pyodide/project). Must be called after writing modules and before
   * any import-resolution pass, or freshly-written project files look "missing"
   * and get mistakenly sent to micropip.
   */
  async invalidateImportCaches(): Promise<void> {
    try {
      await this.python.runPythonAsync('import importlib; importlib.invalidate_caches()');
    } catch {
      // Non-fatal: worst case find_spec misses a just-written module.
    }
  }

  /**
   * Check if a module is already available (built-in or installed).
   */
  private async isModuleAvailable(moduleName: string): Promise<boolean> {
    try {
      // sys.modules first: a loaded module may have no spec, and find_spec
      // raises ValueError for it (`import __main__` used to be sent to micropip).
      const result = runPythonIsolated(this.python, `
import sys
import importlib.util

def _is_available(name):
    if name in sys.modules:
        return True
    try:
        return importlib.util.find_spec(name) is not None
    except (ImportError, ValueError):
        return False

_is_available(${JSON.stringify(moduleName)})
`);
      return result === true;
    } catch {
      return false;
    }
  }

  /**
   * Build dynamic import-to-package name map from Pyodide lockfile.
   * Handles PIL→Pillow, cv2→opencv-python, etc. without hardcoding.
   */
  private buildImportToPackageMap(): Map<string, string> {
    const map = new Map<string, string>();
    try {
      // Pyodide 0.27 exposes the lockfile as the `lockfile` ATTRIBUTE (object with
      // `.packages`), not a `lockfileInfo()` function — calling the latter throws
      // "lockfileInfo is not a function". Fall back to the internal `_api.lockfile`.
      const lockfile = (this.python.lockfile ?? (this.python as any)._api?.lockfile) as
        | PyodideLockfile
        | undefined;
      for (const [pkgName, pkgInfo] of Object.entries(lockfile?.packages ?? {})) {
        for (const importName of pkgInfo.imports ?? []) {
          if (importName !== pkgName) {
            map.set(importName, pkgName);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to build import-to-package map from lockfile:', e);
    }
    return map;
  }

  /**
   * Resolve a Python import name to its actual package name.
   * E.g. 'PIL' → 'Pillow', 'cv2' → 'opencv-python'.
   */
  private getPackageName(moduleName: string): string {
    if (!this.importToPackageMap) {
      this.importToPackageMap = this.buildImportToPackageMap();
    }
    return this.importToPackageMap.get(moduleName) || moduleName;
  }

  /**
   * Install packages via micropip and track them in the journal.
   * Skips modules that are already available (built-in or installed).
   */
  private async installPackages(packages: string[]): Promise<void> {
    for (const pkg of packages) {
      // Check if module is already available (built-in or installed)
      const isAvailable = await this.isModuleAvailable(pkg);
      if (isAvailable) {
        // Mark as loaded without installing
        if (this.fileJournal) {
          this.fileJournal.logPythonPackageLoaded(pkg);
        } else if (this.loadedPackages) {
          this.loadedPackages.add(pkg);
        }
        continue;
      }

      try {
        const packageName = this.getPackageName(pkg);
        console.log(`📦 Installing package: ${packageName}${packageName !== pkg ? ` (import: ${pkg})` : ''}`);
        await this.python.runPythonAsync(
          `import micropip; await micropip.install('${packageName}')`
        );
        console.log(`✅ Installed package: ${packageName}`);
      } catch (installError: any) {
        const errorMsg = `Package installation failed for '${pkg}': ${installError?.message || installError}`;
        console.error(`[RUNTIME] ${errorMsg}`);
        throw new Error(errorMsg);
      }

      if (this.fileJournal) {
        this.fileJournal.logPythonPackageLoaded(pkg);
      } else if (this.loadedPackages) {
        this.loadedPackages.add(pkg);
      }
    }
  }

  /**
   * Wait for a Python package to become available for import.
   * Uses polling with importlib to verify package is ready.
   *
   * @param packageName - Name of the package to wait for (e.g., 'pandas', 'numpy')
   * @param timeoutMs - Maximum time to wait in milliseconds (default: 5000)
   * @throws Error if package doesn't become available within timeout
   */
  async waitForPackage(packageName: string, timeoutMs: number = 5000): Promise<void> {
    const startTime = Date.now();
    let lastError: any = null;

    while (Date.now() - startTime < timeoutMs) {
      try {
        // Use importlib.util.find_spec to check if package is importable
        const result = await this.python.runPythonAsync(`
import importlib.util
spec = importlib.util.find_spec('${packageName}')
spec is not None
        `.trim());

        if (result) {
          return; // Package is ready!
        }
      } catch (error) {
        lastError = error;
      }

      // Wait 100ms before next check
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Timeout reached
    throw new Error(
      `Package '${packageName}' failed to load within ${timeoutMs}ms.\n` +
      `This usually means:\n` +
      `1. The package is not available in Pyodide (check: https://pyodide.org/en/stable/usage/packages-in-pyodide.html)\n` +
      `2. Network issues preventing download\n` +
      `3. Package name typo in import statement\n` +
      `Last error: ${lastError?.message || 'Unknown'}`
    );
  }

  /**
   * Process imports from main content and all .py files in working directory
   * @param content - Python code content to analyze
   * @param jsModules - Set of JS module names to filter
   */
  async processAllImports(content: string, jsModules: Set<string>): Promise<void> {

    const allImports = `${content}\n`; // Include main content imports

    await this.processLoadingPackagesFromImports(allImports, undefined, jsModules);
  }

  /**
   * Regex-based fallback for extracting import module names from Python code.
   * Used when AST-based analysis fails (e.g. syntax errors in step code).
   * Matches `import X` and `from X import ...` patterns.
   */
  static extractImportsViaRegex(content: string): string[] {
    const deps = new Set<string>();
    // Extract top-level package names from import statements line-by-line
    for (const line of content.split('\n')) {
      const trimmed = line.trimStart();
      let pkg: string | undefined;
      if (trimmed.startsWith('import ')) {
        pkg = trimmed.slice(7).split(/[\s.,]/)[0];
      } else if (trimmed.startsWith('from ')) {
        pkg = trimmed.slice(5).split(/[\s.]/)[0];
      }
      if (pkg && /^\w+$/.test(pkg)) {
        deps.add(pkg);
      }
    }
    return [...deps];
  }

}
